const START_DRIVE_STATE = "START_DRIVE";
const START_DRIVE_TYPE = "StartDrive";
const KICK_OFF_STATE = "KICK_OFF";
const PLAY_PENALTY_STATE = "PLAY_PENALTY";
export const END_DRIVE_TIME_STATE = "END_DRIVE_TIME";
const END_DRIVE_TIME_TYPE = "EndDriveTime";

const isStartDriveFragment = (fragment) =>
    fragment?._type === START_DRIVE_TYPE ||
    fragment?.associatedState === START_DRIVE_STATE;

// EndDriveTime fragments predate the implicit-drive model (class removed in
// PSTATS-842). Their class is gone from the serializer's mappings, so they
// revive as plain objects and crash any fragment method dispatch (summarize,
// getFieldPosition). Drive end times are recomputed gap-closed by
// DriveService.computeDriveTotalTimes, so the fragment carries no data worth
// keeping. The play's `_state.history` keeps its END_DRIVE_TIME entry so the
// remaining fragments' `associatedHistoryIndex` links stay valid; the wizard
// renders no step for a history state with no fragment.
export const isEndDriveTimeFragment = (fragment) =>
    fragment?._type === END_DRIVE_TIME_TYPE ||
    fragment?.associatedState === END_DRIVE_TIME_STATE;

const isDef = (value) => value !== undefined && value !== null;

const playHistory = (play) => play?._state?.history || [];

/**
 * Re-derive each play's `driveId` from the legacy drive boundary markers, fixing
 * the foreign-drive corruption where a penalty (or kickoff) left unassigned was
 * yanked into a *later* drive by the unscoped `_linkKO` sweep.
 *
 * The "Start of Drive" plays and the kickoffs that precede them are ground-truth
 * boundaries: each drive effectively begins at its leading kickoff (the kickoff
 * between the previous StartDrive and this one) or, when there is none (a drive
 * opened by a turnover/fumble), at the StartDrive itself. Every play falls in the
 * index window of exactly one drive; this returns the corrections needed.
 *
 * Only kickoff and penalty plays are corrected — they are the sole categories
 * the buggy `_linkKO` sweep ever mis-assigned. Scrimmage/PAT plays carrying a
 * foreign driveId are a different artifact (a possession change with no
 * StartDrive/drive recorded, e.g. a fumble-return score) and are left untouched
 * so their stats are not re-credited to the wrong drive. Plays before the first
 * drive and null "filler" plays (timeouts, field updates) are also left as-is so
 * drive index bounds are not widened.
 *
 * Pure and idempotent: a payload with correct links yields no changes.
 *
 * @param {Array<{id:string,index:number,driveId:?string,isStartDrive:boolean,
 *   isKickOff:boolean,isPlayPenalty:boolean}>} items
 * @returns {Array<{id:string,driveId:string}>} corrections
 */
export const deriveDriveLinks = (items) => {
    const sorted = [...items].sort((a, b) => a.index - b.index);
    const startDrives = sorted.filter((p) => p.isStartDrive && p.driveId);
    if (!startDrives.length) {
        return [];
    }

    const windows = startDrives.map((sd, k) => {
        const prevStartIndex = k > 0 ? startDrives[k - 1].index : -Infinity;
        const leadingKickoffs = sorted.filter(
            (p) => p.isKickOff && p.index > prevStartIndex && p.index < sd.index
        );
        const start = leadingKickoffs.length
            ? Math.min(...leadingKickoffs.map((p) => p.index))
            : sd.index;
        return { driveId: sd.driveId, start };
    });

    const changes = [];
    for (const play of sorted) {
        if (play.isStartDrive) {
            continue;
        }
        let windowDriveId = null;
        for (const w of windows) {
            if (w.start <= play.index) {
                windowDriveId = w.driveId;
            } else {
                break;
            }
        }
        if (windowDriveId === null || play.driveId === windowDriveId) {
            continue;
        }
        if (play.isKickOff || play.isPlayPenalty) {
            // A kickoff or penalty whose link disagrees with its drive window
            // (foreign-driveId corruption) or that was left unassigned.
            changes.push({ id: play.id, driveId: windowDriveId });
        }
    }
    return changes;
};

/**
 * Strip legacy "Start of Drive" plays from a serialized event payload so games
 * synced from / imported into the implicit-drive model carry no synthetic drive
 * plays. For each StartDrive play it backfills the referenced drive's start
 * fields from the play's context snapshot when missing (older drives predate the
 * `startYard` field), then drops the StartDrive plays and their fragments and
 * reindexes the remaining plays contiguously. Legacy EndDriveTime fragments are
 * always dropped (see isEndDriveTimeFragment), even when the payload has no
 * StartDrive plays.
 *
 * Operates on plain serialized objects (pre-`revive`) and is idempotent: a
 * payload with no StartDrive plays is returned unchanged.
 *
 * @param {Array<object>} plays
 * @param {Array<object>} fragments
 * @param {Array<object>} drives
 * @returns {{plays: Array<object>, fragments: Array<object>, drives: Array<object>}}
 */
export const normalizeLegacyDrives = (
    plays = [],
    fragments = [],
    drives = []
) => {
    if (fragments.some(isEndDriveTimeFragment)) {
        fragments = fragments.filter(
            (fragment) => !isEndDriveTimeFragment(fragment)
        );
    }

    const startDriveFragments = fragments.filter(isStartDriveFragment);
    const startDrivePlayIds = new Set(
        startDriveFragments.map((fragment) => fragment.playId)
    );

    const startDrivePlays = plays.filter(
        (play) =>
            startDrivePlayIds.has(play.id) ||
            (play._state?.history || []).includes(START_DRIVE_STATE)
    );

    if (!startDrivePlays.length) {
        return { plays, fragments, drives };
    }

    // Repair corrupted play->drive links (foreign-driveId penalties/kickoffs)
    // while the StartDrive boundary markers are still present, before they are
    // stripped below. StartDrive detection reuses the fragment-or-history set
    // computed above; kickoff/penalty are read from the play state history.
    const startDrivePlayIdSet = new Set(startDrivePlays.map((play) => play.id));
    const driveLinkChanges = deriveDriveLinks(
        plays.map((play) => {
            const history = playHistory(play);
            return {
                id: play.id,
                index: play.index,
                driveId: play.driveId,
                isStartDrive: startDrivePlayIdSet.has(play.id),
                isKickOff: history.includes(KICK_OFF_STATE),
                isPlayPenalty: history.includes(PLAY_PENALTY_STATE)
            };
        })
    );
    if (driveLinkChanges.length) {
        const changeById = new Map(
            driveLinkChanges.map((c) => [c.id, c.driveId])
        );
        plays.forEach((play) => {
            if (changeById.has(play.id)) {
                play.driveId = changeById.get(play.id);
            }
        });
    }

    startDrivePlays.forEach((play) => {
        const drive = drives.find((d) => d.id === play.driveId);
        const snapshot = play.contextSnapshot;
        if (!drive || !snapshot) {
            return;
        }
        if (!isDef(drive.startYard) && isDef(snapshot.fieldPosition)) {
            drive.startYard = snapshot.fieldPosition;
        }
        if (!isDef(drive.startPeriod) && isDef(snapshot.currentPeriod)) {
            drive.startPeriod = snapshot.currentPeriod;
        }
        if (
            !isDef(drive.homeTeamHasPossession) &&
            isDef(snapshot.homeTeamHasPossession)
        ) {
            drive.homeTeamHasPossession = snapshot.homeTeamHasPossession;
        }
    });

    const removedPlayIds = new Set(startDrivePlays.map((play) => play.id));

    const sortedPlays = plays
        .filter((play) => !removedPlayIds.has(play.id))
        .sort((a, b) => a.index - b.index);
    const baseIndex = sortedPlays.length
        ? Math.min(...sortedPlays.map((play) => play.index))
        : 0;
    const remainingPlays = sortedPlays.map((play, position) => ({
        ...play,
        index: baseIndex + position
    }));

    const remainingFragments = fragments.filter(
        (fragment) =>
            !isStartDriveFragment(fragment) &&
            !removedPlayIds.has(fragment.playId)
    );

    return { plays: remainingPlays, fragments: remainingFragments, drives };
};
